/**
 * Ryan O'Dowd
 * 2018-04-15
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Image,
  Linking,
  Platform,
  ScrollView,
  Share,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  fetchTranslations,
  fetchWebStatsMsSpent,
  fetchWebStatsNumVersesReviewed,
} from '@utils/store/actions';
import Accordion from '@components/common/Accordion';
import Clipboard from '@react-native-clipboard/clipboard';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import Icon from '@react-native-vector-icons/material-design-icons';
import LicensingInfo from '@components/LicensingInfo';
import RateDialog from '@components/RateDialog';
import ScreenWrapper from '@containers/ScreenWrapper';
import {
  Snackbar,
} from 'react-native-paper';
import Text from '@components/common/Text';
import apps from '@utils/globals/apps';
import {
  format as dateFnsFormat,
} from 'date-fns';
import fb from '@utils/firebase';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const About = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  const [_copyLinkSnackbarVisible, setCopyLinkSnackbarVisible] = useState(false);
  const msSpent = hooks.useTypedSelector((state) => state.webStatsMsSpent);
  const versesReviewed = hooks.useTypedSelector((state) => state.webStatsNumVersesReviewed);
  const [_rateDialogOpen, setRateDialogOpen] = useState(false);

  const translationsReducer = hooks.useTypedSelector((state) => state.translations);
  const translations = useMemo(() => {
    return translationsReducer.filter((t) => !t.disabled);
  }, [translationsReducer]);

  useEffect(() => {
    dispatch(fetchTranslations());
    dispatch(fetchWebStatsMsSpent());
    dispatch(fetchWebStatsNumVersesReviewed());
  }, [dispatch]);

  let storeLink = Platform.OS === 'ios' ? globals.appStoreUrl : globals.playStoreUrl;
  if (Platform.OS === 'web') {
    storeLink = globals.webUrl;
  }

  const _getShareOptions = () => {
    return [
      {
        name: i18n('share'),
        onPress: async () => {
          fb.logEvent(fb.analytics, 'share_link', {location: 'about_page'});
          try {
            await Share.share({
              message: i18n('shareOptionsMessage'),
              url: storeLink,
              title: i18n('shareOptionsTitle'),
            }, {
              // Android only:
              dialogTitle: i18n('shareWrinkly'),
            });
          } catch (error) {
            if (error.message === 'Share canceled') {
              return;
            }
            fb.recordError(fb.crashlytics, error);
          }
        },
        iconName: 'share',
      },
      {
        name: i18n('copyLink'),
        onPress: () => {
          setCopyLinkSnackbarVisible(true);
          fb.logEvent(fb.analytics, 'copy_link', {location: 'about_page'});
          Clipboard.setString(storeLink);
        },
        iconName: 'link-variant',
      },
      {
        name: i18n('rate'),
        onPress: () => {
          fb.logEvent(fb.analytics, 'rate_wrinkly', {location: 'about_page'});
          if (Platform.OS === 'web') {
            setRateDialogOpen(true);
          } else {
            Linking.openURL(storeLink);
          }
        },
        iconName: 'star',
        color: '#FFC125',
      },
    ];
  };

  const shareButtons = _getShareOptions().map((shareOption) => {
    return (
      <View
        key={`${shareOption.name}${shareOption.iconName}`}
        style={styles.shareOption}
      >
        <View style={styles.buttonAndTextWrapper}>
          <TouchableOpacity
            style={styles.buttonImageWrapper}
            onPress={shareOption.onPress}
          >
            <Icon
              style={[styles.buttonImage, styles.buttonIcon, shareOption.color && {color: shareOption.color}]}
              name={shareOption.iconName as any}
            />
          </TouchableOpacity>
          <Text
            style={styles.buttonNameText}
            numberOfLines={1}
            adjustsFontSizeToFit={true}
          >
            {shareOption.name}
          </Text>
        </View>
      </View>
    );
  });

  const otherAppButtons = apps(i18n).map((app) => {
    const buttonChildren = (app.image
      ? (
        <Image
          style={styles.buttonImage}
          resizeMode='cover'
          source={app.image}
        />
      ) : (
        <Icon
          style={[styles.buttonImage, styles.buttonIcon]}
          name={app.iconName as any}
        />
      )
    );

    return (
      <React.Fragment key={`${app.name}${app.iconName}`}>
        {app.verticalDividerBefore && <View style={styles.verticalDivider} />}
        <View style={styles.buttonAndTextWrapper}>
          <TouchableOpacity
            style={styles.buttonImageWrapper}
            onPress={() => Linking.openURL(app.link)}
          >
            {buttonChildren}
          </TouchableOpacity>
          <Text
            style={styles.buttonNameText}
            numberOfLines={1}
            adjustsFontSizeToFit={true}
          >
            {app.name}
          </Text>
        </View>
        {app.verticalDividerAfter && <View style={styles.verticalDivider} />}
      </React.Fragment>
    );
  });

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        title={i18n('aboutUs')}
      />
      <FullScreenScrollView
        contentContainerStyle={styles.innerContainerWrapper}
        style={styles.innerContainer}
      >
        <View style={styles.aboutContainer}>
          <Text style={styles.headerText}>{i18n('whoWeAre')}</Text>
          <Text style={styles.text}>{i18n('whoWeAreBody')}</Text>
        </View>

        <View style={styles.aboutContainer}>
          <Text style={styles.headerText}>{i18n('originStoryTitle')}</Text>
          <Text style={styles.text}>{i18n('originStoryBody')}</Text>
        </View>

        <View style={styles.aboutContainer}>
          <Text style={styles.headerText}>{i18n('missionAndVision')}</Text>
          <Text style={[styles.text, styles.lineOne]}><Text style={styles.accentTitle}>{i18n('mission')}: </Text>{i18n('missionBody')}</Text>
          <Text style={styles.text}><Text style={styles.accentTitle}>{i18n('vision')}: </Text>{i18n('visionBody')}</Text>
        </View>

        <View style={styles.aboutContainer}>
          <Text style={styles.headerText}>{i18n('impact')}</Text>
          <Text style={styles.text}>{i18n('impactBody',
            <Text style={styles.statText}>{utilities.humanReadableTime(msSpent)}</Text>,
            <Text style={styles.statText}>{utilities.humanReadableNumber(versesReviewed)} {i18n('versesLowercase')}</Text>,
            styles.text,
          )}</Text>
        </View>

        <Text style={styles.headerText}>{i18n('support')}</Text>
        <Text style={styles.text}>{i18n('supportBodyOne')}</Text>
        <Text style={styles.text}>{i18n('supportBodyTwo', Platform.OS === 'web' ? 'App Store or Play Store' : (Platform.OS === 'ios' ? 'App Store' : 'Play Store'))}</Text>{/* @TODO: i18n */}

        <View>
          <Text style={styles.headerText}>{i18n('share')}</Text>
          <View style={styles.actions}>
            <View style={styles.shareButtonsWrapper}>
              {shareButtons}
            </View>
          </View>
        </View>

        {/* @TODO: add "tip the developers" section */}

        <View>
          <Text style={styles.headerText}>Explore</Text>
          <ScrollView
            contentContainerStyle={styles.actions}
            horizontal={true}
          >
            {otherAppButtons}
          </ScrollView>
        </View>

        <View style={styles.horizontalDivider} />
        <Accordion// @TODO: should this be a ListItem instead?
          title={<Text style={styles.headerText}>{i18n('licensing')}</Text>}
        >
          {translations.map((t) => <LicensingInfo key={t.id} translationId={t.id} />)}
        </Accordion>

        <View style={styles.horizontalDivider} />
        <Text style={styles.headerText}>{i18n('info')}</Text>
        <View style={styles.otherInfoWrapper}>
          <Text style={styles.text}>
            {i18n('appVersion')}: {globals.appVersionNumber}
          </Text>
          <Text style={styles.text}>
            {'\u00A9'} {dateFnsFormat(new Date(), 'yyyy', {locale: i18n.dateFnsLocale})} Oakwood Software Consulting, Inc.
          </Text>
        </View>

        <Snackbar
          // @TODO: change placement on web
          visible={_copyLinkSnackbarVisible}
          onDismiss={() => setCopyLinkSnackbarVisible(false)}
          duration={5000}
        >
          {i18n('downloadLinkCopied')}
        </Snackbar>
      </FullScreenScrollView>
      {Platform.OS === 'web' && _rateDialogOpen &&
        <RateDialog onClose={() => setRateDialogOpen(false)} />
      }
    </ScreenWrapper>
  );
};

export default About;
